// Voice Calls — operator phone-call console. Mirrors the WhatsApp page
// (routes/whatsapp.tsx) but for outbound Plivo voice calls: a member list on
// the left (grouped from voice.list_recent), each member's call history on the
// right, a daily Placed/Answered/Failed KPI strip, and a "Call" composer that
// places a voice call via notify.send (channels:['voice']). Reuses the .wa-*
// layout classes so the full-height fix applies to both pages.

import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { current_language } from '../i18n/index.js';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

interface VoiceCall {
  id: string;
  call_id: string;
  kind: string;
  subscriber_phone: string;
  subscriber_id: string | null;
  subscriber_name_ta?: string | null;
  subscriber_name_en?: string | null;
  status: string;
  duration_seconds: number;
  dtmf_captured: string | null;
  script_text?: string | null;
  placed_at: string;
}

interface CallThread {
  key: string;
  subscriber_id: string | null;
  subscriber_phone: string;
  name_ta: string | null;
  name_en: string | null;
  last: VoiceCall;
  count: number;
}

const ANSWERED = new Set(['completed']);
const FAILED = new Set(['failed', 'busy', 'no_answer', 'cancelled']);

function initials(name: string | null | undefined, phone: string | null | undefined): string {
  const src = (name && name.trim() ? name : phone) || '';
  const p = src.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '··';
  if (p.length === 1) return p[0]!.slice(-2).toUpperCase();
  return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
}

function fmt_duration(secs: number): string {
  if (!secs || secs <= 0) return '';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function status_badge(status: string | null | undefined): { icon: string; color: string } {
  if (status && ANSWERED.has(status)) return { icon: '✓', color: 'var(--good)' };
  if (status && FAILED.has(status)) return { icon: '✗', color: 'var(--bad)' };
  return { icon: '⏳', color: 'var(--ink-3)' }; // queued / ringing / in_progress / unknown
}

/** Null-safe "snake_case" → "snake case" for display. */
function label(s: string | null | undefined, fallback = '—'): string {
  return (s ?? fallback).replace(/_/g, ' ');
}

const TODAY_IST = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
function is_today(iso: string): boolean {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) === TODAY_IST;
}

export function CallsPage(): ReactElement {
  const lang = current_language();
  const qc = useQueryClient();
  const [selected, set_selected] = useState<string | null>(null); // thread key
  const [draft, set_draft] = useState('');
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  const recent_q = useQuery({
    queryKey: ['voice-recent'],
    queryFn: () => orpc.voice.list_recent({ limit: 200 }),
    refetchInterval: 60_000,
  });
  const recent = (recent_q.data?.calls ?? []) as VoiceCall[];

  // Group the flat recent feed into one thread per member (newest first).
  const threads = useMemo<CallThread[]>(() => {
    const map = new Map<string, CallThread>();
    for (const c of recent) {
      const key = c.subscriber_id ?? c.subscriber_phone;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          key,
          subscriber_id: c.subscriber_id,
          subscriber_phone: c.subscriber_phone,
          name_ta: c.subscriber_name_ta ?? null,
          name_en: c.subscriber_name_en ?? null,
          last: c,
          count: 1,
        });
      } else {
        existing.count += 1;
        if (new Date(c.placed_at) > new Date(existing.last.placed_at)) existing.last = c;
      }
    }
    return [...map.values()].sort(
      (a, b) => new Date(b.last.placed_at).getTime() - new Date(a.last.placed_at).getTime(),
    );
  }, [recent]);

  // Today's KPI strip.
  const kpi = useMemo(() => {
    const today = recent.filter((c) => is_today(c.placed_at));
    return {
      placed: today.length,
      answered: today.filter((c) => ANSWERED.has(c.status)).length,
      failed: today.filter((c) => FAILED.has(c.status)).length,
    };
  }, [recent]);

  useEffect(() => {
    if (!selected && threads.length > 0) set_selected(threads[0]!.key);
  }, [threads, selected]);

  const conv = threads.find((th) => th.key === selected) ?? null;

  // Per-member call history. When the member has a subscriber_id we fetch the
  // full history; otherwise (raw-phone call) we fall back to the recent feed.
  const history_q = useQuery({
    queryKey: ['voice-history', conv?.subscriber_id],
    queryFn: () => orpc.voice.list_for_subscriber({ subscriber_id: conv!.subscriber_id!, limit: 100 }),
    enabled: !!conv?.subscriber_id,
  });
  const history: VoiceCall[] = conv?.subscriber_id
    ? ((history_q.data?.calls ?? []) as VoiceCall[])
    : recent.filter((c) => conv && c.subscriber_phone === conv.subscriber_phone);

  const call_m = useMutation({
    mutationFn: () => {
      if (!conv?.subscriber_id) throw new Error('no subscriber for this call');
      return orpc.notify.send({
        subscriber_id: conv.subscriber_id,
        intent: 'custom',
        channels: ['voice'],
        custom_body_text: draft.trim(),
      });
    },
    onSuccess: () => {
      set_draft('');
      void qc.invalidateQueries({ queryKey: ['voice-history', conv?.subscriber_id] });
      void qc.invalidateQueries({ queryKey: ['voice-recent'] });
    },
  });

  const name_of = (th: { name_ta: string | null; name_en: string | null; subscriber_phone: string }): string =>
    (lang === 'ta' ? th.name_ta : th.name_en) ?? th.subscriber_phone;

  return (
    <div className="page full" style={{ padding: 0, height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="page-head" style={{ flexShrink: 0, margin: 0, padding: '8px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>{t('குரல் அழைப்புகள்', 'Voice Calls')}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t('Plivo குரல்', 'Plivo Voice')}</div>
        </div>
        {recent_q.data && (
          <div style={{ display: 'flex', gap: 14, fontSize: 11 }}>
            {([['placed', t('இன்று அழைப்பு', 'placed')], ['answered', t('பதிலளித்தது', 'answered')], ['failed', t('தோல்வி', 'failed')]] as const).map(([k, label]) => (
              <div key={k} style={{ textAlign: 'right' }}>
                <div style={{ color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10 }}>{label}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: k === 'failed' && kpi.failed > 0 ? 'var(--bad)' : 'var(--ink)' }}>{kpi[k]}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', padding: '6px 22px 14px' }}>
        <div className="card" style={{ overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="wa-stage">
            {/* Member list */}
            <div className="wa-side">
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)', fontSize: 11, color: 'var(--ink-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('அழைப்புகள்', 'Recent calls')} {threads.length > 0 && `(${threads.length})`}
              </div>
              {recent_q.isLoading && <div style={{ padding: 14, fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>}
              {!recent_q.isLoading && threads.length === 0 && (
                <div style={{ padding: 14, fontSize: 13, color: 'var(--ink-3)' }}>{t('இன்னும் அழைப்புகள் இல்லை', 'No calls yet')}</div>
              )}
              {threads.map((th) => {
                const badge = status_badge(th.last.status);
                return (
                  <div
                    key={th.key}
                    className={'wa-side-row' + (selected === th.key ? ' on' : '')}
                    onClick={() => set_selected(th.key)}
                  >
                    <div className="ava-sm">{initials(lang === 'ta' ? th.name_ta : th.name_en, th.subscriber_phone)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 12.5, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name_of(th)}</span>
                        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{th.count}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                        <span style={{ color: badge.color }}>{badge.icon}</span> {label(th.last.status, 'pending')}
                        {' · '}
                        {new Date(th.last.placed_at).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Call history + composer */}
            <div className="wa-conv">
              {!conv && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                  {threads.length === 0 ? t('அழைப்புகள் இல்லை', 'No calls') : t('ஒரு உறுப்பினரைத் தேர்ந்தெடுக்கவும்', 'Select a member')}
                </div>
              )}
              {conv && (
                <>
                  <div className="wa-conv-head">
                    <div className="ava-sm">{initials(lang === 'ta' ? conv.name_ta : conv.name_en, conv.subscriber_phone)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontWeight: 500 }}>{name_of(conv)}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }} className="mono">{conv.subscriber_phone}</div>
                    </div>
                  </div>
                  <div className="wa-stream" style={{ background: 'var(--bg)' }}>
                    {history_q.isLoading && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>}
                    {history.length === 0 && !history_q.isLoading && (
                      <div style={{ alignSelf: 'center', color: 'var(--ink-3)', fontSize: 12.5, padding: 8 }}>{t('அழைப்பு வரலாறு இல்லை', 'No call history')}</div>
                    )}
                    {history.map((c) => {
                      const badge = status_badge(c.status);
                      const dur = fmt_duration(c.duration_seconds);
                      return (
                        <div key={c.id} className="wa-bubble out" style={{ maxWidth: '88%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: badge.color, fontSize: 13 }}>{badge.icon}</span>
                            <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{label(c.kind, 'call')}</span>
                            {dur && <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 11 }}>· {dur}</span>}
                          </div>
                          {c.script_text && (
                            <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 3 }}>{c.script_text}</div>
                          )}
                          <div className="meta">
                            {new Date(c.placed_at).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                            {' · '}
                            <span style={{ color: badge.color }}>{label(c.status, 'pending')}</span>
                            {c.dtmf_captured && <span style={{ color: 'var(--good)' }}> · pressed {c.dtmf_captured}</span>}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ alignSelf: 'center', fontSize: 10.5, color: 'var(--ink-3)', padding: 4 }}>
                      {t('குரல் அழைப்பு · Plivo', 'Voice call · Plivo')}
                    </div>
                  </div>
                  <div className="wa-input">
                    <input
                      value={draft}
                      onChange={(e) => set_draft(e.target.value)}
                      disabled={!conv.subscriber_id || call_m.isPending}
                      placeholder={
                        conv.subscriber_id
                          ? t('பேச வேண்டிய செய்தி (ஆங்கிலம்)…', 'Message to speak (English)…')
                          : t('உறுப்பினர் தெரியவில்லை', 'Member not on file')
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && draft.trim() && !call_m.isPending) call_m.mutate();
                      }}
                    />
                    <button
                      type="button"
                      className="btn primary sm"
                      disabled={!draft.trim() || !conv.subscriber_id || call_m.isPending}
                      onClick={() => call_m.mutate()}
                    >
                      {call_m.isPending ? '…' : t('அழை', 'Call')}
                    </button>
                  </div>
                  {call_m.error && (
                    <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--bad)' }}>{friendly_error(call_m.error)}</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
